import { useEffect, useState } from 'react'
import { IS_PRODUCTION, SITE_VERSION } from '@/lib/site'
import { useI18n } from '@/i18n/I18nProvider'

/**
 * The stripe every non-production deployment wears.
 *
 * Preview URLs get shared — with colleagues, then in a thread, then to someone
 * outside the company — and by the third hop nobody remembers it was a draft.
 * The `noindex` in the head keeps drafts out of search; this keeps them out of
 * conversations. It renders nothing in production, so it costs the live site a
 * single `if`.
 */

/**
 * Fetched client-side rather than baked in at build time, and that is
 * deliberate rather than a shortcut. The count is genuinely a build-time
 * constant (it cannot change without a rebuild), but making it one the same
 * way `SITE_VERSION` is one would mean computing it before `vite build` runs
 * and threading it through as an env var, or serializing it into every
 * hydrated page for the client to read back without re-importing the full
 * content tree. Both are real solutions; this is the cheap one, and it is
 * already the exact pattern `__editor.js` uses for the same manifest file
 * (see its `checkManifest()`) — so this is one more reader of an endpoint
 * that already exists, not new infrastructure. The cost is real but small:
 * the count is blank until this effect resolves, and it never appears at all
 * on the pages that never hydrate (117 of them — imported legacy content, see
 * `src/legacy.ts`) because no client JS runs there to fetch anything. Both
 * ROUTES pages and the reports/e-books do hydrate, which covers where a
 * reviewer actually lands.
 */
function useChecksSummary(): { total: number; content: number; asset: number } | null {
  const [summary, setSummary] = useState<{ total: number; content: number; asset: number } | null>(
    null,
  )
  useEffect(() => {
    if (IS_PRODUCTION) return
    let cancelled = false
    fetch('/build-manifest.json', { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((manifest) => {
        if (!cancelled) setSummary(manifest?.checksSummary ?? null)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])
  return summary
}

export default function PreviewBanner() {
  const { t } = useI18n()
  const checks = useChecksSummary()
  if (IS_PRODUCTION) return null

  return (
    <div className="bg-foreground text-background">
      <div className="site-container flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-xs">
        <span className="rounded-full bg-background/15 px-2 py-0.5 font-semibold uppercase tracking-wide">
          {t.preview.badge} {SITE_VERSION}
        </span>
        <span className="opacity-80">{t.preview.notice}</span>
        {/* A distinct, warm colour rather than the neutral badge above — Kagan,
            2026-08-27: "bir number stating the open numbers ... with a
            different color". Amber against the dark stripe reads as "needs
            attention" the way the badge and notice deliberately do not. Only
            rendered once the fetch resolves with something to say, so a
            resolved build (or a page that never hydrates) shows nothing extra
            rather than a wrong or perpetually-loading number. */}
        {checks && checks.total > 0 && (
          <a
            href="/__checks"
            className="ml-auto rounded-full bg-amber-400 px-2 py-0.5 font-semibold text-amber-950 hover:bg-amber-300"
            title={`${checks.content} content · ${checks.asset} report/e-book — click to read them`}
          >
            {checks.total} open gate item{checks.total === 1 ? '' : 's'}
          </a>
        )}
      </div>
    </div>
  )
}
